if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.sectionId = this.id ? this.id.replace('MediaGallery-', '') : null;

        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (button) {
              button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
            }
          });

          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.bindVariantChangeEvents();

        window.setTimeout(() => {
          this.applyInitialVariantFilter();
        }, 0);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.getFirstVisibleMedia();

        if (!activeMedia) return;

        const selectedAlt = activeMedia.classList.contains('product__media-item--full')
          ? 'shared'
          : (activeMedia.dataset.variantAlt || '').trim().toLowerCase();
        this.filterMediaByVariantAlt(selectedAlt);

        const refreshedActiveMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([hidden])`) ||
          this.getFirstVisibleMedia();

        if (!refreshedActiveMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        refreshedActiveMedia.classList.add('is-active');

        if (prepend) {
          if (refreshedActiveMedia.parentElement.firstChild !== refreshedActiveMedia) {
            refreshedActiveMedia.parentElement.prepend(refreshedActiveMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${refreshedActiveMedia.dataset.mediaId}"]`
            );
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            refreshedActiveMedia.parentElement.scrollTo({ left: refreshedActiveMedia.offsetLeft });
          }

          const activeMediaRect = refreshedActiveMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(refreshedActiveMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${refreshedActiveMedia.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(refreshedActiveMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      getFirstVisibleMedia() {
        return this.elements.viewer.querySelector('[data-media-id]:not([hidden])');
      }

      filterMediaByVariantAlt(selectedAlt) {
        const normalizedSelectedAlt = (selectedAlt || '').trim().toLowerCase();
        const showAllMedia = normalizedSelectedAlt === '' || normalizedSelectedAlt === 'shared';

        const mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        const thumbnailItems = this.elements.thumbnails
          ? this.elements.thumbnails.querySelectorAll('[data-target]')
          : [];

        mediaItems.forEach((item) => {
          const itemAlt = (item.dataset.variantAlt || '').trim().toLowerCase();
          const isSharedMedia = item.classList.contains('product__media-item--full');

          const shouldShow =
            showAllMedia ||
            isSharedMedia ||
            itemAlt === '' ||
            itemAlt === 'shared' ||
            itemAlt === normalizedSelectedAlt;

          item.hidden = !shouldShow;
          item.classList.toggle('hidden', !shouldShow);
        });

        thumbnailItems.forEach((item) => {
          const itemAlt = (item.dataset.variantAlt || '').trim().toLowerCase();
          const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${item.dataset.target}"]`);
          const isSharedMedia = targetMedia?.classList.contains('product__media-item--full');

          const shouldShow =
            showAllMedia ||
            isSharedMedia ||
            itemAlt === '' ||
            itemAlt === 'shared' ||
            itemAlt === normalizedSelectedAlt;

          item.hidden = !shouldShow;
          item.classList.toggle('hidden', !shouldShow);
        });

        const activeItem = this.elements.viewer.querySelector('.is-active');
        if (activeItem && activeItem.hidden) {
          activeItem.classList.remove('is-active');
          const fallback = this.getFirstVisibleMedia();
          if (fallback) fallback.classList.add('is-active');
        }
      }

      bindVariantChangeEvents() {
        const productContainer =
          this.closest('section') ||
          this.closest('.shopify-section') ||
          document;

        productContainer.addEventListener('change', (event) => {
          const target = event.target;
          if (!target) return;

          const isVariantInput =
            target.matches('input[type="radio"]') ||
            target.matches('select') ||
            target.matches('[name^="options["]') ||
            target.matches('[name="Color"]') ||
            target.matches('[name="options[Color]"]');

          if (!isVariantInput) return;

          window.setTimeout(() => {
            this.applyInitialVariantFilter();
          }, 50);
        });
      }

      applyInitialVariantFilter() {
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const selectedAlt = activeMedia.classList.contains('product__media-item--full')
          ? 'shared'
          : (activeMedia.dataset.variantAlt || '').trim().toLowerCase();
        this.filterMediaByVariantAlt(selectedAlt);

        const visibleActive =
          this.elements.viewer.querySelector('.product__media-item.is-active:not([hidden])') ||
          this.getFirstVisibleMedia();

        if (!visibleActive) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          item.classList.remove('is-active');
        });

        visibleActive.classList.add('is-active');

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${visibleActive.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(activeThumbnail);
        }
      }
    }
  );
}